import { Box, CircularProgress, Modal, Typography } from "@mui/material"
import React, { useCallback, useRef, useState } from "react"

import { FuturePricingPackage } from "./FuturePricingPackage"
import { PricingAndShippingTerms } from "./PricingAndShippingTerms"

import { SuccessTemplate } from "@/components/common/Modals/SuccessTemplate"
import { InfoTable } from "@/components/InfoTable/InfoTable"
import type { Product } from "@/graphql/codegen/graphql"
import { useGetPricingPackage } from "@/screens/Products/hooks/useGetPricingPackage"

interface ExpandedContentProps {
  product: Product
  sourceId: string
  expanded?: boolean
}

export const ProductPricingTerms: React.FC<ExpandedContentProps> = ({ product, sourceId, expanded = true }) => {
  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const [openFutureModal, setOpenFutureModal] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  // Passing all mandatory query params along with effectiveNow:true to fetch current pricing packages
  const { pricingList, pricingListFetching, reexecuteQueryWithReset } = useGetPricingPackage({
    filterSpecs: {
      companyId: product.company?.companyId ?? "",
      productId: product.productId,
      sourceCompanyLocationAssociationId: sourceId,
    },
    expanded,
  })

  const { currentPricing, futurePricing } = pricingList

  const handleCancel = useCallback(() => {
    setOpenSuccessModal(false)
  }, [])

  const handleConfirmCancel = useCallback(() => {
    setOpenSuccessModal(false)
    setOpenFutureModal(true)
  }, [])

  const handleFutureSubmit = useCallback((showAddMoreModal: boolean) => {
    setOpenSuccessModal(showAddMoreModal)
  }, [])

  if (pricingListFetching) {
    return (
      <Box className='flex size-full items-center justify-center'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box ref={contentRef}>
      <Box className='w-full items-center border-b border-gray-200 p-4 pr-6 last:border-0'>
        {/* If sourceId is not present, it means that the pricing package is not available for this location */}
        {!sourceId ? (
          <InfoTable
            title='Current Pricing'
            chipData={[
              { label: "Effective", value: "-" },
              { label: "Valid Until", value: "-" },
            ]}
            emptyRowMessage='Price to obtain'
            extraInfo={[
              { label: "Freight", value: "-" },
              { label: "Incoterms", value: "-" },
            ]}
          />
        ) : (
          <>
            {/* TODO: AK-2834 */}
            <PricingAndShippingTerms
              product={product}
              sourceId={sourceId}
              isEmptyFuturePricing={!currentPricing && futurePricing.length === 0}
              currentPricing={currentPricing}
              reexecuteQueryWithReset={reexecuteQueryWithReset}
            />
            <FuturePricingPackage
              product={product}
              pricingList={futurePricing}
              sourceId={sourceId}
              isEmptyFuturePricing={!currentPricing && futurePricing.length === 0}
              reexecuteQueryWithReset={reexecuteQueryWithReset}
              onSubmit={handleFutureSubmit}
              openFutureModal={openFutureModal}
              onFuturePricingSubmit={setOpenFutureModal}
            />
          </>
        )}
        <Modal open={openSuccessModal} onClose={handleCancel}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              boxShadow: 24,
              width: 576,
              borderRadius: 2,
            }}
          >
            <SuccessTemplate
              title='Future Pricing Successfully Added'
              message={
                <>
                  You're product future price has successfully been added.
                  <Typography variant='body2' className='text-base font-bold'>
                    Would you like to add additional future Pricing now?
                  </Typography>
                </>
              }
              goBackText='Skip & add later'
              submitText='Add Additional Future Pricing'
              onClose={setOpenSuccessModal}
              confirmCancel={handleCancel}
              onConfirm={handleConfirmCancel}
            />
          </Box>
        </Modal>
      </Box>
    </Box>
  )
}
